import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody } from '../design-system/Modal';
import {
  listOrgMembersForSharing,
  shareArtifact,
  listArtifactShares,
  revokeArtifactAccess,
  type OrgMemberForSharing,
  type AccessGrant,
  type GrantPermission,
} from '../../api/services/sharing';
import toast from 'react-hot-toast';

interface ShareButtonProps {
  artifactId: string;
  className?: string;
}

export const ShareButton: React.FC<ShareButtonProps> = ({ artifactId, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [members, setMembers] = useState<OrgMemberForSharing[]>([]);
  const [grants, setGrants] = useState<AccessGrant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [selectedPermission, setSelectedPermission] = useState<GrantPermission>('view');

  // Load members and current shares when modal opens
  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, artifactId]);

  const loadData = async () => {
    setIsLoadingMembers(true);
    try {
      const [membersData, sharesData] = await Promise.all([
        listOrgMembersForSharing(),
        listArtifactShares(artifactId),
      ]);
      setMembers(membersData.members);
      setGrants(sharesData.grants);
    } catch (error) {
      console.error('Failed to load sharing data:', error);
      toast.error('Failed to load sharing options');
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const handleShare = async () => {
    if (!selectedMemberId) {
      toast.error('Please select a member to share with');
      return;
    }

    setIsLoading(true);
    try {
      const member = members.find((m) => m.user_id === selectedMemberId);
      if (!member) {
        toast.error('Selected member not found');
        return;
      }

      await shareArtifact(artifactId, {
        grantee_type: 'user',
        grantee_id: member.user_id,
        permission: selectedPermission,
      });

      toast.success(`Shared with ${member.email}`);
      setSelectedMemberId('');
      setSelectedPermission('view');
      await loadData(); // Refresh the list
    } catch (error: any) {
      console.error('Failed to share artifact:', error);
      const message =
        error?.json?.error === 'cross_org_sharing_forbidden'
          ? 'Cannot share outside your organization'
          : 'Failed to share artifact';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevoke = async (grant: AccessGrant) => {
    if (
      !window.confirm(
        `Revoke access for ${getGranteeEmail(grant)}? They will no longer be able to access this document.`,
      )
    ) {
      return;
    }

    setIsLoading(true);
    try {
      await revokeArtifactAccess(artifactId, {
        grantee_type: grant.grantee_type,
        grantee_id: grant.grantee_id,
      });

      toast.success('Access revoked');
      await loadData(); // Refresh the list
    } catch (error) {
      console.error('Failed to revoke access:', error);
      toast.error('Failed to revoke access');
    } finally {
      setIsLoading(false);
    }
  };

  const getGranteeEmail = (grant: AccessGrant): string => {
    if (grant.grantee_type === 'user') {
      const member = members.find((m) => m.user_id === grant.grantee_id);
      return member?.email || grant.grantee_id;
    }
    return grant.grantee_id;
  };

  const getGranteeDisplay = (grant: AccessGrant): string => {
    if (grant.grantee_type === 'user') {
      return getGranteeEmail(grant);
    }
    return `${grant.grantee_type}: ${grant.grantee_id}`;
  };

  const isAlreadyShared = (userId: string): boolean => {
    return grants.some((g) => g.grantee_type === 'user' && g.grantee_id === userId);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold transition hover:shadow-sm bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50 h-[22px] ${className}`}
        title="Share document"
        aria-label="Share document with organization members"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
          />
        </svg>
        Share
      </button>

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="md">
        <ModalHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-neutral-900">Share Document</h2>
            <button
              onClick={() => setIsOpen(false)}
              className="text-neutral-400 hover:text-neutral-600 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </ModalHeader>
        <ModalBody className="rounded-b-xl sm:rounded-b-2xl">
          <div className="py-4 space-y-6">
            {/* Share with new member */}
            <div>
              <h3 className="text-sm font-semibold text-neutral-900 mb-3">
                Share with organization member
              </h3>
              <div className="space-y-3">
                <div>
                  <label
                    htmlFor="member-select"
                    className="block text-xs font-medium text-neutral-700 mb-1.5"
                  >
                    Select member
                  </label>
                  <select
                    id="member-select"
                    value={selectedMemberId}
                    onChange={(e) => setSelectedMemberId(e.target.value)}
                    disabled={isLoadingMembers || isLoading}
                    className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Choose a member...</option>
                    {members.map((member) => (
                      <option key={member.user_id} value={member.user_id}>
                        {member.email}
                        {isAlreadyShared(member.user_id) && ' (already shared)'}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="permission-select"
                    className="block text-xs font-medium text-neutral-700 mb-1.5"
                  >
                    Permission level
                  </label>
                  <select
                    id="permission-select"
                    value={selectedPermission}
                    onChange={(e) => setSelectedPermission(e.target.value as GrantPermission)}
                    disabled={isLoading}
                    className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="view">View only</option>
                    <option value="edit">Can edit</option>
                  </select>
                </div>

                <button
                  type="button"
                  onClick={handleShare}
                  disabled={
                    !selectedMemberId ||
                    isLoading ||
                    isLoadingMembers ||
                    isAlreadyShared(selectedMemberId)
                  }
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Sharing...' : 'Share'}
                </button>
              </div>
            </div>

            {/* Current shares */}
            <div>
              <h3 className="text-sm font-semibold text-neutral-900 mb-3">Shared with</h3>
              {isLoadingMembers ? (
                <div className="text-sm text-neutral-400 py-4">Loading...</div>
              ) : grants.length === 0 ? (
                <div className="text-sm text-neutral-400 py-4">
                  No one has access yet. Share with organization members above.
                </div>
              ) : (
                <div className="space-y-2">
                  {grants.map((grant) => (
                    <div
                      key={grant.id}
                      className="flex items-center justify-between px-3 py-2 bg-neutral-50 rounded-lg border border-neutral-200"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-neutral-900 truncate">
                          {getGranteeDisplay(grant)}
                        </div>
                        <div className="text-xs text-neutral-500 mt-0.5">
                          {grant.permission === 'edit' ? 'Can edit' : 'View only'}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRevoke(grant)}
                        disabled={isLoading}
                        className="ml-3 px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Revoke access"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ShareButton;
